import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BLOOD_GROUPS, OPIATE_SUBSTANCES, OPIATE_UNITS, SEXES, SUBSTANCE_LABEL,
  drugFields, measurementFields, patientFields, recordFields,
} from '@pharmacia/core'
import { ApiError, get, post } from '../lib/api.ts'
import type { UploadedFile } from '../lib/upload.ts'
import { PageHeader } from '../components/AppShell.tsx'
import { CameraCapture } from '../components/CameraCapture.tsx'
import { useToast } from '../components/Toast.tsx'
import { Button, Field, Input, Select, Spinner } from '../components/ui.tsx'
import { IconCheck } from '../components/icons.tsx'

const STEPS = ['Identification', 'Body measurement', 'Drug quantity', 'Declaration'] as const

type Str = Record<string, string>
const BLANK_PATIENT: Str = {
  fullName: '', fatherName: '', age: '', sex: '', maritalStatus: '', education: '',
  caste: '', occupation: '', phone: '', addressLine1: '', tehsil: '', district: '',
  state: 'Rajasthan', pincode: '', bloodGroup: '', aadhaar: '',
}
const BLANK_MEASURE: Str = { weightKg: '', bpSystolic: '', bpDiastolic: '', pulse: '', spo2: '' }
const BLANK_DRUG: Str = {
  opiateAmount: '', opiateUnit: 'g', opiateSubstance: 'opium',
  tablets: '', addictionYears: '', addictionMonths: '',
}
// Only remarks remain: the referral and ticket fields were not wanted.
const BLANK_RECORD: Str = { remarks: '' }

const num = (v: string) => (v.trim() === '' ? null : Number(v))
const txt = (v: string) => (v.trim() === '' ? null : v.trim())

export function RegisterPage() {
  const navigate = useNavigate()
  const toast = useToast()

  const [step, setStep] = useState(0)
  const [patient, setPatient] = useState<Str>(BLANK_PATIENT)
  const [measure, setMeasure] = useState<Str>(BLANK_MEASURE)
  const [drug, setDrug] = useState<Str>(BLANK_DRUG)
  const [record, setRecord] = useState<Str>(BLANK_RECORD)
  const [photo, setPhoto] = useState<UploadedFile | null>(null)
  const [aadhaarFront, setAadhaarFront] = useState<UploadedFile | null>(null)
  const [aadhaarBack, setAadhaarBack] = useState<UploadedFile | null>(null)
  // Signatures are parked until the capture method is settled. The API,
  // the schema and the SignaturePad component all still support them, so
  // switching them back on is re-adding one section.
  const [agreed, setAgreed] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [declaration, setDeclaration] = useState<{ version: string; body: string } | null>(null)
  const [done, setDone] = useState<{ serialNo: string; mrn: string } | null>(null)

  // The ids are fixed for the life of this wizard, so a retry after a lost
  // response replays the same ones and returns the existing record rather
  // than putting the patient on the register twice.
  const ids = useMemo(() => ({ id: crypto.randomUUID(), patientId: crypto.randomUUID() }), [])

  useEffect(() => {
    get<{ version: string; body: string }>('/api/declaration/current')
      .then(setDeclaration)
      .catch(() => toast.fail('Could not load the declaration text'))
  }, [toast])

  useEffect(() => () => {
    for (const f of [photo, aadhaarFront, aadhaarBack]) {
      if (f) URL.revokeObjectURL(f.previewUrl)
    }
  }, [photo, aadhaarFront, aadhaarBack])

  const bind = (state: Str, set: (s: Str) => void) => (k: string) =>
    ({ value: state[k] ?? '', onChange: (e: { target: { value: string } }) => set({ ...state, [k]: e.target.value }) })

  const p = bind(patient, setPatient)
  const m = bind(measure, setMeasure)
  const d = bind(drug, setDrug)
  const r = bind(record, setRecord)

  // Each step validates with the SAME schema the API will use, so a
  // problem is caught on the step that caused it rather than at the end.
  function validateStep(i: number): boolean {
    const payloads = [
      () => patientFields.safeParse(toPatient()),
      () => measurementFields.safeParse(toMeasure()),
      () => drugFields.safeParse(toDrug()),
      () => recordFields.safeParse(toRecord()),
    ]
    const res = payloads[i]!()
    if (res.success) { setErrors({}); return true }
    const next: Record<string, string> = {}
    for (const issue of res.error.issues) next[issue.path.join('.')] = issue.message
    setErrors(next)
    toast.fail('Some fields need attention')
    return false
  }

  const toPatient = () => ({
    fullName: patient['fullName']!.trim(), fatherName: txt(patient['fatherName']!),
    age: num(patient['age']!), sex: txt(patient['sex']!) as never,
    maritalStatus: txt(patient['maritalStatus']!), education: txt(patient['education']!),
    caste: txt(patient['caste']!), occupation: txt(patient['occupation']!),
    phone: txt(patient['phone']!), addressLine1: txt(patient['addressLine1']!),
    tehsil: txt(patient['tehsil']!), district: txt(patient['district']!),
    state: txt(patient['state']!), pincode: txt(patient['pincode']!),
    bloodGroup: txt(patient['bloodGroup']!) as never,
    aadhaar: patient['aadhaar']!.trim(),
  })
  const toMeasure = () => ({
    weightKg: num(measure['weightKg']!), bpSystolic: num(measure['bpSystolic']!),
    bpDiastolic: num(measure['bpDiastolic']!), pulse: num(measure['pulse']!),
    spo2: num(measure['spo2']!),
  })
  const toDrug = () => ({
    opiateAmount: num(drug['opiateAmount']!),
    opiateUnit: (txt(drug['opiateUnit']!) ?? null) as never,
    opiateSubstance: (txt(drug['opiateSubstance']!) ?? null) as never,
    tablets: txt(drug['tablets']!),
    addictionYears: num(drug['addictionYears']!), addictionMonths: num(drug['addictionMonths']!),
  })
  const toRecord = () => ({ remarks: txt(record['remarks']!) })

  function next() { if (validateStep(step)) setStep(s => Math.min(3, s + 1)) }
  function back() { setErrors({}); setStep(s => Math.max(0, s - 1)) }

  async function submit() {
    if (!declaration) return
    if (!validateStep(3)) return
    setBusy(true)
    try {
      const files = [photo, aadhaarFront, aadhaarBack].filter(Boolean).map(f => ({
        fileId: f!.fileId, kind: f!.kind, mimeType: f!.mimeType, sizeBytes: f!.sizeBytes,
      }))
      const res = await post<{ serialNo: string; mrn: string }>('/api/registrations', {
        ...ids,
        patient: toPatient(),
        measurement: toMeasure(),
        drug: toDrug(),
        record: toRecord(),
        declarationVersion: declaration.version,
        files,
      })
      setDone({ serialNo: res.serialNo, mrn: res.mrn })
    } catch (e) {
      const err = e as ApiError
      if (err.code === 'VALIDATION_FAILED') {
        const next: Record<string, string> = {}
        for (const dt of err.details ?? []) next[dt.field.split('.').pop()!] = dt.message
        setErrors(next)
        toast.fail('Some fields need attention', 'Check the earlier steps.')
      } else {
        toast.fail('Could not register', err.message)
      }
    } finally { setBusy(false) }
  }

  if (done) {
    return (
      <>
        <PageHeader title="Registered" />
        <div className="content">
          <div className="page-max stack" style={{ gap: 'var(--s-5)', maxWidth: 620 }}>
            <div className="mrn-banner">
              <IconCheck size={18} />
              <div className="grow">
                <div style={{ fontWeight: 600, color: 'var(--ink)' }}>Entry created</div>
                <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                  Write both numbers on the paper form
                </div>
              </div>
            </div>
            <div className="card"><div className="card-body stack" style={{ gap: 'var(--s-4)' }}>
              <div><div className="dim" style={{ fontSize: 'var(--text-xs)' }}>SERIAL NO.</div>
                <div className="mono" style={{ fontSize: 'var(--text-xl)', fontWeight: 650 }}>{done.serialNo}</div></div>
              <div><div className="dim" style={{ fontSize: 'var(--text-xs)' }}>PATIENT NO. (MRN)</div>
                <div className="mono" style={{ fontSize: 'var(--text-xl)', fontWeight: 650 }}>{done.mrn}</div></div>
            </div></div>
            <div className="row" style={{ gap: 'var(--s-3)' }}>
              <Button variant="primary" onClick={() => window.location.assign('/register')}>
                Register another
              </Button>
              <Button onClick={() => navigate('/patients')}>Back to patients</Button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader title="New registration">
        <span className="dim" style={{ fontSize: 'var(--text-sm)' }}>
          Step {step + 1} of 4
        </span>
      </PageHeader>

      <div className="content">
        <div className="page-max stack" style={{ gap: 0, maxWidth: 780 }}>
          <div className="stepper">
            {STEPS.map((label, i) => (
              <div key={label} className={`step ${i === step ? 'now' : i < step ? 'done' : ''}`}>
                <span className="n">{i < step ? '✓' : i + 1}</span>{label}
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-body">
              {step === 0 ? (
                <>
                  <section className="form-section">
                    <h3>Identity</h3>
                    <div className="form-grid">
                      <div className="span-2">
                        <Field label="Name" htmlFor="r-name" error={errors['fullName']}>
                          <Input id="r-name" {...p('fullName')} required maxLength={160} autoFocus
                            aria-invalid={Boolean(errors['fullName'])} />
                        </Field>
                      </div>
                      <Field label="Father's name" error={errors['fatherName']}>
                        <Input {...p('fatherName')} maxLength={160} />
                      </Field>
                      <Field label="Age" error={errors['age']}>
                        <Input {...p('age')} type="number" min={0} max={130} />
                      </Field>
                      <Field label="Sex" error={errors['sex']}>
                        <Select {...p('sex')}>
                          <option value="">—</option>
                          {SEXES.map(s => <option key={s} value={s}>{s[0]!.toUpperCase() + s.slice(1)}</option>)}
                        </Select>
                      </Field>
                      <Field label="Blood group" error={errors['bloodGroup']}>
                        <Select {...p('bloodGroup')}>
                          <option value="">—</option>
                          {BLOOD_GROUPS.map(b => <option key={b} value={b}>{b}</option>)}
                        </Select>
                      </Field>
                      <Field label="Marital status" error={errors['maritalStatus']}>
                        <Input {...p('maritalStatus')} maxLength={40} />
                      </Field>
                      <Field label="Education" error={errors['education']}>
                        <Input {...p('education')} maxLength={80} />
                      </Field>
                      <Field label="Caste" error={errors['caste']}>
                        <Input {...p('caste')} maxLength={80} />
                      </Field>
                      <Field label="Occupation" error={errors['occupation']}>
                        <Input {...p('occupation')} maxLength={80} />
                      </Field>
                    </div>
                  </section>

                  <section className="form-section">
                    <h3>Contact &amp; address</h3>
                    <div className="form-grid">
                      <Field label="Phone" error={errors['phone']}>
                        <Input {...p('phone')} type="tel" maxLength={20}
                          aria-invalid={Boolean(errors['phone'])} />
                      </Field>
                      <Field label="Pincode" error={errors['pincode']} hint="Six digits">
                        <Input {...p('pincode')} inputMode="numeric" maxLength={6}
                          aria-invalid={Boolean(errors['pincode'])} />
                      </Field>
                      <div className="span-2">
                        <Field label="Aadhaar number" error={errors['aadhaar']}
                          hint="Twelve digits. The check digit is verified as you type — no number leaves this building.">
                          <Input {...p('aadhaar')} inputMode="numeric" maxLength={14}
                            placeholder="1234 5678 9012"
                            aria-invalid={Boolean(errors['aadhaar'])} />
                        </Field>
                      </div>

                      <div className="span-2">
                        <Field label="Address" error={errors['addressLine1']}>
                          <Input {...p('addressLine1')} maxLength={240} />
                        </Field>
                      </div>
                      <Field label="Tehsil" error={errors['tehsil']}><Input {...p('tehsil')} maxLength={80} /></Field>
                      <Field label="District" error={errors['district']}><Input {...p('district')} maxLength={80} /></Field>
                      <Field label="State" error={errors['state']}><Input {...p('state')} maxLength={80} /></Field>
                    </div>
                  </section>

                  <section className="form-section">
                    <h3>Photograph</h3>
                    <CameraCapture kind="photo" value={photo} onChange={setPhoto} />
                  </section>

                  <section className="form-section">
                    <h3>Aadhaar card</h3>
                    <div className="capture-pair">
                      <CameraCapture kind="aadhaar_front" label="Front" aspect="8 / 5"
                        value={aadhaarFront} onChange={setAadhaarFront} />
                      <CameraCapture kind="aadhaar_back" label="Back" aspect="8 / 5"
                        value={aadhaarBack} onChange={setAadhaarBack} />
                    </div>
                    <p className="muted" style={{ marginTop: 'var(--s-3)', fontSize: 'var(--text-sm)' }}>
                      The number is checked for a valid check digit; these photographs
                      are what records the card the operator actually saw.
                    </p>
                  </section>
                </>
              ) : null}

              {step === 1 ? (
                <section className="form-section">
                  <h3>Readings taken today</h3>
                  <div className="form-grid">
                    <Field label="Present weight (kg)" error={errors['weightKg']} hint="Optional">
                      <Input {...m('weightKg')} type="number" step="0.1" min={0} autoFocus />
                    </Field>
                    <div />
                    <Field label="Blood pressure — systolic" error={errors['bpSystolic']} hint="Required">
                      <Input {...m('bpSystolic')} type="number" min={40} max={300} required
                        aria-invalid={Boolean(errors['bpSystolic'])} />
                    </Field>
                    <Field label="Blood pressure — diastolic" error={errors['bpDiastolic']} hint="Required">
                      <Input {...m('bpDiastolic')} type="number" min={20} max={200} required
                        aria-invalid={Boolean(errors['bpDiastolic'])} />
                    </Field>
                    <Field label="Pulse (per minute)" error={errors['pulse']} hint="Required">
                      <Input {...m('pulse')} type="number" min={20} max={250} required
                        aria-invalid={Boolean(errors['pulse'])} />
                    </Field>
                    <Field label="Oxygen — SpO₂ (%)" error={errors['spo2']} hint="Required">
                      <Input {...m('spo2')} type="number" min={50} max={100} required
                        aria-invalid={Boolean(errors['spo2'])} />
                    </Field>
                  </div>
                  <p className="muted" style={{ marginTop: 'var(--s-4)', fontSize: 'var(--text-sm)' }}>
                    This is day zero of the thirty-day series. Readings at day 5, 10,
                    15, 20, 25 and 30 are recorded against this entry later.
                  </p>
                </section>
              ) : null}

              {step === 2 ? (
                <section className="form-section">
                  <h3>Addiction</h3>
                  <div className="form-grid">
                    <div className="span-2">
                      <Field label="Quantity of opiate drug or other narcotics"
                        error={errors['opiateAmount']}
                        hint="Reads as: 10 Kg per month, opium">
                        <div className="row" style={{ gap: 'var(--s-2)' }}>
                          <Input {...d('opiateAmount')} type="number" step="0.001" min={0}
                            autoFocus style={{ flex: 2, minWidth: 0 }} />
                          <Select {...d('opiateUnit')} style={{ flex: 1, minWidth: 0 }} aria-label="Unit">
                            {OPIATE_UNITS.map(u => <option key={u} value={u}>{u === 'kg' ? 'Kg' : 'g'}</option>)}
                          </Select>
                          <span className="dim" style={{ whiteSpace: 'nowrap' }}>per month</span>
                          <Select {...d('opiateSubstance')} style={{ flex: 1.3, minWidth: 0 }}
                            aria-label="Substance">
                            {OPIATE_SUBSTANCES.map(x => (
                              <option key={x} value={x}>{SUBSTANCE_LABEL[x]}</option>
                            ))}
                          </Select>
                        </div>
                      </Field>
                    </div>
                    <div className="span-2">
                      <Field label="Any tablet" error={errors['tablets']}
                        hint="Name and quantity, as stated by the patient">
                        <Input {...d('tablets')} maxLength={400} />
                      </Field>
                    </div>
                    <Field label="Addiction period — years" error={errors['addictionYears']}>
                      <Input {...d('addictionYears')} type="number" min={0} max={120} />
                    </Field>
                    <Field label="Addiction period — months" error={errors['addictionMonths']}
                      hint="0 to 11">
                      <Input {...d('addictionMonths')} type="number" min={0} max={11} />
                    </Field>
                  </div>
                </section>
              ) : null}

              {step === 3 ? (
                <>
                  <section className="form-section">
                    <h3>Remarks</h3>
                    <div className="form-grid">
                      <div className="span-2">
                        <Field label="Remarks" error={errors['remarks']}>
                          <Input {...r('remarks')} maxLength={1000} autoFocus />
                        </Field>
                      </div>
                    </div>
                  </section>

                  <section className="form-section">
                    <h3>Declaration</h3>
                    {declaration ? (
                      <>
                        <div className="declaration">
                          {declaration.body}
                          <span className="ver">Version {declaration.version}</span>
                        </div>
                        <label className="row" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-4)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={agreed}
                            onChange={e => setAgreed(e.target.checked)} />
                          <span>
                            Read aloud to the patient, who agreed and signed the
                            paper form.
                          </span>
                        </label>
                      </>
                    ) : <Spinner />}
                  </section>
                </>
              ) : null}
            </div>

            <div className="form-actions">
              {step > 0 ? <Button type="button" onClick={back} disabled={busy}>Back</Button> : null}
              <span className="grow" />
              <Button type="button" variant="ghost" onClick={() => navigate('/patients')} disabled={busy}>
                Cancel
              </Button>
              {step < 3 ? (
                <Button type="button" variant="primary" onClick={next}>Continue</Button>
              ) : (
                <Button type="button" variant="primary" onClick={submit}
                  loading={busy} disabled={!agreed || !declaration}>
                  Create register entry
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
